import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Stethoscope, ShieldCheck, Heart, Mail, ExternalLink, AlertCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSEO } from "@/lib/seo";

/** Public clinician profile. Indexable (clinicians want to be found). */
export default function TherapistDetail() {
  const { slug } = useParams();
  const [t, setT] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/therapists/${slug}`);
        setT(data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
  }, [slug]);

  useSEO({
    title: t ? `${t.full_name}, ${t.credentials} — SonSpeak clinician directory` : "SonSpeak clinician directory",
    description: t?.bio?.slice(0, 160),
    path: `/therapists/${slug}`,
  });

  return (
    <div className="min-h-screen dotted-bg" data-testid="therapist-detail-page">
      <header className="px-5 sm:px-10 py-5 border-b-[3px] border-[color:var(--ink)] flex items-center justify-between bg-[color:var(--cream)]">
        <Link to="/therapists" className="pill-btn"><ArrowLeft size={14} /> Directory</Link>
        <Link to="/register" className="pill-btn primary">Sign up</Link>
      </header>

      <main className="max-w-2xl mx-auto px-5 sm:px-10 py-12">
        {error && (
          <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-4 mb-4 flex gap-2" data-testid="therapist-error">
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <div>{error}</div>
          </div>
        )}
        {t && (
          <article className="brutal-card bg-white p-6 sm:p-8" data-testid="therapist-card">
            <div className="display text-xs uppercase tracking-[0.3em] text-[color:var(--coral-deep)] font-bold mb-3 flex items-center gap-2">
              <ShieldCheck size={12} /> Hand-reviewed listing
            </div>
            <h1 className="display text-3xl sm:text-4xl font-extrabold mb-1 flex items-center gap-2">
              <Stethoscope size={26} className="text-[color:var(--coral-deep)]" />
              {t.full_name}
            </h1>
            <div className="text-sm text-[color:var(--ink-soft)] mb-5">
              {t.credentials} · Licensed in {t.license_state}
            </div>

            {(t.specialties || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-5">
                {t.specialties.map((s) => (
                  <span key={s} className="text-xs px-2 py-0.5 rounded-full border-[2px] border-[color:var(--ink)] bg-[color:var(--paper-muted)] font-bold">{s}</span>
                ))}
                {t.sliding_scale && (
                  <span className="text-xs px-2 py-0.5 rounded-full border-[2px] border-[color:var(--ink)] bg-[color:var(--gold)] font-bold flex items-center gap-1">
                    <Heart size={10} /> Sliding scale
                  </span>
                )}
              </div>
            )}

            <div className="text-base sm:text-lg leading-relaxed whitespace-pre-wrap mb-5">{t.bio}</div>

            <div className="brutal-card bg-[color:var(--paper-muted)] p-3 flex flex-wrap items-center gap-2">
              {t.contact_email && (
                <a href={`mailto:${t.contact_email}`} className="pill-btn" data-testid="therapist-email">
                  <Mail size={14} /> Email {t.full_name.split(" ")[0]}
                </a>
              )}
              {t.website && (
                <a href={t.website} target="_blank" rel="noreferrer" className="pill-btn" data-testid="therapist-website">
                  <ExternalLink size={14} /> Website
                </a>
              )}
            </div>
          </article>
        )}
      </main>
    </div>
  );
}
